import { BACKEND_SERVER_NINJA } from '@/settings';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { StatusEnum } from '../common-types';

export interface IBrand {
  owner: number;
  name: string;
  typings: string;
  added: Date;
}

export interface INewBrand {
  name: string;
  typings: string;
}

export interface IBrandGroup {
  id: number;
  project: number;
  name: string;
  color: string;
  description: string;
  brands: IBrand[];
  created: Date;
  changed: Date;
}

export interface INewBrandGroup {
  project: number;
  name: string;
  color: string;
  description: string;
}

export interface IUpdateBrandGroup {
  id: number;
  name?: string;
  description?: string;
  color?: string;
  new_brands?: INewBrand[];
}

export interface IBrandGroupsState {
  brandGroups: IBrandGroup[];
  currentBrandGroup: IBrandGroup | null;
  status: StatusEnum;
  statusText: string | null;
}

const initialState: IBrandGroupsState = {
  brandGroups: [],
  currentBrandGroup: null,
  status: StatusEnum.idle,
  statusText: null,
};

export const getBrandGroups = createAsyncThunk(
  'organization/getBrandGroups',
  async (args: { slug: string; clientId?: number; projectId?: number }) => {
    const params: { client?: number; project?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    if (args.projectId) {
      params.project = args.projectId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/brand_groups`,
      { params }
    );

    return response.data as IBrandGroup[];
  }
);

export const getBrandGroup = createAsyncThunk(
  'organization/getBrandGroup',
  async (args: {
    slug: string;
    brandGroupId: number;
    clientId?: number;
    projectId?: number;
  }) => {
    const params: { client?: number; project?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    if (args.projectId) {
      params.project = args.projectId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/brand_groups/${args.brandGroupId}`,
      { params }
    );
    return response.data as IBrandGroup;
  }
);

export const createBrandGroup = createAsyncThunk(
  'organization/createBrandGroup',
  async (args: { slug: string; brandGroup: INewBrandGroup }) => {
    const response = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/brand_groups`,
      args.brandGroup
    );
    return response.data as IBrandGroup;
  }
);

export const updateBrandGroup = createAsyncThunk(
  'organization/updateBrandGroup',
  async (args: { slug: string; brandGroup: IUpdateBrandGroup }) => {
    const response = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/brand_groups/${args.brandGroup.id}`,
      args.brandGroup
    );
    return response.data as IBrandGroup;
  }
);

export const deleteBrandGroup = createAsyncThunk(
  'organization/deleteBrandGroup',
  async (args: { slug: string; brandGroupId: number }) => {
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/brand_groups/${args.brandGroupId}`
    );
    return args.brandGroupId;
  }
);

const brandGroupsSlice = createSlice({
  name: 'brandGroups',
  initialState,
  reducers: {
    setBrandGroup: (state, action: PayloadAction<IBrandGroup | null>) => {
      state.currentBrandGroup = action.payload;
    },
    setBrandGroupIdle: (state) => {
      state.status = StatusEnum.idle;
      state.statusText = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBrandGroups.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getBrandGroups.fulfilled,
        (state, action: PayloadAction<IBrandGroup[]>) => {
          state.status = StatusEnum.succeeded;
          state.brandGroups = action.payload;
          if (!state.currentBrandGroup) {
            state.currentBrandGroup = action.payload[0] ?? null;
          }
        }
      )
      .addCase(getBrandGroups.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(getBrandGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getBrandGroup.fulfilled,
        (state, action: PayloadAction<IBrandGroup>) => {
          state.status = StatusEnum.succeeded;
          state.currentBrandGroup = action.payload;
        }
      )
      .addCase(getBrandGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(createBrandGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        createBrandGroup.fulfilled,
        (state, action: PayloadAction<IBrandGroup>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Бренд создан';
          state.currentBrandGroup = action.payload;
          state.brandGroups.push(action.payload);
          toast.success(state.statusText);
        }
      )
      .addCase(createBrandGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(updateBrandGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateBrandGroup.fulfilled,
        (state, action: PayloadAction<IBrandGroup>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Бренд обновлен';
          const updatedBrandGroupIndex = state.brandGroups.findIndex(
            (c) => c.id === action.payload.id
          );
          if (updatedBrandGroupIndex !== -1) {
            state.brandGroups[updatedBrandGroupIndex] = action.payload;
          }
          toast.success(state.statusText);
        }
      )
      .addCase(updateBrandGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(deleteBrandGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        deleteBrandGroup.fulfilled,
        (state, action: PayloadAction<number | undefined>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Бренд удален';
          if (typeof action.payload !== 'undefined') {
            const deletedBrandGroupIndex = state.brandGroups.findIndex(
              (c) => c.id === action.payload
            );
            if (deletedBrandGroupIndex >= 0) {
              state.brandGroups.splice(deletedBrandGroupIndex, 1);
            }
          }
          toast.success(state.statusText);
        }
      )
      .addCase(deleteBrandGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      });
  },
});

export const { setBrandGroup, setBrandGroupIdle } = brandGroupsSlice.actions;

export default brandGroupsSlice.reducer;
